/**
 * Verkauf von Wiegeartikeln an der Kasse (Verkaufsart „gewicht“: Preis je kg aus
 * `bruttoCent` des Artikels, Gewicht in ganzen Gramm, Rundung im gemeinsamen Modul des
 * RKSV-Kerns). Rein und ohne React – die Oberfläche (Gewichtsdialog) ruft nur diese
 * Funktionen. Die Kasse hängt sonst nicht am Paket `@bilanx/rksv-kern`; das Gewichtsmodul ist
 * abhängigkeitsfrei und wird direkt aus den Quellen eingebunden, damit Kasse und Server
 * dieselbe Rundung rechnen.
 */
import {
  formatiereKg,
  GEWICHT_MAX_GRAMM,
  gewichtPreisCent,
  grammAusEingabe,
  mengeKgAusGramm,
} from '../../../packages/rksv-kern/src/gewicht.js';
import { kgMengenText } from '../../../packages/rksv-kern/src/mengen.js';
import { positionAus, type Schnellwahl } from './schnellwahl';
import type { Position } from './warenkorb';

export { formatiereKg, GEWICHT_MAX_GRAMM, gewichtPreisCent, grammAusEingabe };

/** Verkaufsart eines Artikels; Freipositionen („Menge oder Betrag“) sind kein Artikel. */
export type Verkaufsart = 'stueck' | 'gewicht';

/** Verkaufsart aus dem Artikelstamm – fehlend oder unbekannt = 'stueck' (Bestand unverändert). */
export function verkaufsartVon(x: unknown): Verkaufsart {
  return x === 'gewicht' ? 'gewicht' : 'stueck';
}

/** Kachel eines Wiegeartikels: öffnet den Gewichtsdialog statt direkt zu bonieren. */
export function istWiegeKachel(k: Pick<Schnellwahl, 'verkaufsart'>): boolean {
  return k.verkaufsart === 'gewicht';
}

/** Bonzeile eines Wiegeartikels (Menge in kg). */
export function istGewichtsPosition(p: Position): boolean {
  return p.einheit === 'kg';
}

/**
 * Woher ein Gewicht stammt. Heute gibt es nur die Handeingabe im Dialog; Waage und Barcode mit
 * eingebettetem Gewicht (EAN 2x) liefern später eine {@link Gewichtsvorgabe} – ausgewertet wird
 * beides hier ausdrücklich NICHT (nur Schnittstelle).
 *
 * `'vorwahl'`: vor der Artikelwahl eingetipptes Gewicht in kg (`vorwahl.ts`). Es ist bereits
 * geprüft und vollständig – {@link waehleArtikel} legt damit sofort die Position an, ohne
 * Gewichtsdialog.
 */
export type GewichtsHerkunft = 'hand' | 'waage' | 'barcode' | 'vorwahl';

/**
 * Von außen geliefertes Gewicht. Waage und Barcode: der Dialog zeigt es vorbelegt an; Vorwahl:
 * die Position entsteht direkt (siehe {@link waehleArtikel}).
 */
export interface Gewichtsvorgabe {
  gramm: number;
  herkunft: Exclude<GewichtsHerkunft, 'hand'>;
}

/**
 * Spätere Gewichtsquelle (z. B. Waage über die Hardware-Bridge). Liefert null, wenn kein
 * stabiles Gewicht vorliegt – dann bleibt es bei der Handeingabe.
 */
export interface Gewichtsquelle {
  gewicht(signal: AbortSignal): Promise<Gewichtsvorgabe | null>;
}

/** Offene Gewichtsabfrage: welcher Artikel, mit welchem Steuersatzzweig, ggf. vorbelegt. */
export interface WiegeAnfrage {
  kachel: Schnellwahl;
  ausserHaus: boolean;
  vorgabe?: Gewichtsvorgabe;
}

/** Ergebnis einer Artikelauswahl (Kachel, Suche, Scan). */
export type Auswahl =
  | { art: 'bonieren'; position: Position }
  | { art: 'gewicht'; anfrage: WiegeAnfrage }
  | { art: 'ignoriert'; grund: string };

/**
 * Artikelauswahl verteilen: Stückartikel gehen wie bisher mit Menge in den Bon, Wiegeartikel
 * öffnen den Gewichtsdialog. Ist bereits eine Gewichtsabfrage offen, wird jede weitere Auswahl
 * verworfen (z. B. wiederholter Scan desselben Artikels) – so entsteht nie eine Doppelposition
 * und der Bon ändert sich nicht hinter dem Dialog.
 *
 * Vorgabe mit Herkunft `'vorwahl'`: Wiegeartikel gehen ohne Dialog direkt mit
 * {@link positionAusGewicht} in den Bon. Ein Stückartikel mit Gewichtsvorgabe aus der Vorwahl
 * oder ein ungültiges Vorwahlgewicht wird nie still umgedeutet, sondern verworfen.
 */
export function waehleArtikel(
  offen: WiegeAnfrage | null,
  k: Schnellwahl,
  stueckPosition: () => Position,
  ausserHaus: boolean,
  vorgabe?: Gewichtsvorgabe,
): Auswahl {
  if (offen !== null) {
    return { art: 'ignoriert', grund: 'Gewichtseingabe offen – erst abschließen oder abbrechen' };
  }
  const ausVorwahl = vorgabe?.herkunft === 'vorwahl';
  if (!istWiegeKachel(k)) {
    if (ausVorwahl) {
      return { art: 'ignoriert', grund: 'Stückartikel – kein Gewicht möglich' };
    }
    return { art: 'bonieren', position: stueckPosition() };
  }
  if (ausVorwahl) {
    if (
      !Number.isInteger(vorgabe.gramm) ||
      vorgabe.gramm < 1 ||
      vorgabe.gramm > GEWICHT_MAX_GRAMM
    ) {
      return { art: 'ignoriert', grund: 'Ungültiges Gewicht' };
    }
    return { art: 'bonieren', position: positionAusGewicht(k, vorgabe.gramm, ausserHaus) };
  }
  return {
    art: 'gewicht',
    anfrage: { kachel: k, ausserHaus, ...(vorgabe === undefined ? {} : { vorgabe }) },
  };
}

/**
 * Bonposition eines Wiegeartikels: Menge in kg, Betrag nach {@link gewichtPreisCent},
 * Kilopreis als `einzelpreisCent`. Bewusst ohne `einzelCent`: Mengenänderungen über den
 * Stückpreis (round(Einzelpreis × Menge)) gibt es für Gewichtszeilen nicht.
 */
export function positionAusGewicht(k: Schnellwahl, gramm: number, ausserHaus = false): Position {
  const basis = positionAus(k, ausserHaus);
  delete basis.einzelCent;
  return {
    ...basis,
    menge: mengeKgAusGramm(gramm),
    bruttoCent: gewichtPreisCent(gramm, k.bruttoCent),
    einheit: 'kg',
    einzelpreisCent: k.bruttoCent,
  };
}

/** Gramm einer Gewichtszeile aus der Menge in kg (ganzzahlig gerundet gegen Gleitkommareste). */
export function grammAusPosition(p: Position): number {
  return Math.round(p.menge * 1000);
}

/**
 * Bontext einer Gewichtszeile: „0,750 kg × 5,99 €/kg“; null bei Stückzeilen. Derselbe Text wie
 * Kundenanzeige, Druck und Rechnung (`kgMengenText` aus rksv-kern/mengen.ts).
 */
export function gewichtsText(p: Position): string | null {
  if (!istGewichtsPosition(p) || p.einzelpreisCent === undefined) {
    return null;
  }
  return kgMengenText(p);
}

/** Live-Vorschau im Dialog: gültiges Gewicht und Positionsbetrag, sonst null. */
export function gewichtsVorschau(
  text: string,
  kilopreisCent: number,
): { gramm: number; bruttoCent: number } | null {
  const gramm = grammAusEingabe(text);
  if (gramm === null) {
    return null;
  }
  return { gramm, bruttoCent: gewichtPreisCent(gramm, kilopreisCent) };
}

/** Taste des Gewichts-Ziffernblocks bzw. der Hardwaretastatur. */
export type GewichtTaste =
  '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | ',' | 'loeschen' | 'leeren';

/**
 * Eingabetext nach einer Taste. Höchstens zwei Stellen vor und drei nach dem Komma, ein
 * Komma, keine führenden Nullen außer „0,“ – Tasten, die das verletzen, ändern nichts
 * (eingegeben wird nie mehr, als {@link grammAusEingabe} annimmt).
 */
export function gewichtEingabe(text: string, taste: GewichtTaste): string {
  if (taste === 'leeren') {
    return '';
  }
  if (taste === 'loeschen') {
    return text.slice(0, -1);
  }
  const [ganz = '', nachkomma] = text.split(',');
  if (taste === ',') {
    if (nachkomma !== undefined) {
      return text;
    }
    return `${ganz === '' ? '0' : ganz},`;
  }
  if (nachkomma !== undefined) {
    return nachkomma.length >= 3 ? text : `${text}${taste}`;
  }
  if (ganz === '0') {
    return taste; // „0“ dann „7“ → „7“
  }
  return ganz.length >= 2 ? text : `${text}${taste}`;
}

/** Vorbelegung des Eingabefelds aus Gramm (Waage/Barcode, später): 750 → '0,750'. */
export function eingabeAusGramm(gramm: number): string {
  return formatiereKg(gramm).replace(' kg', '');
}
